import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { InformationBoxComponent } from '@principle-theorem/ng-principle-shared';
import {
  CustomReportDatePickers,
  IDateFormData,
} from '@principle-theorem/ng-reporting';
import {
  DateRangeFormComponent,
  NgDialogModule,
  validFormGroupChanges$,
} from '@principle-theorem/ng-shared';
import { CampaignCostEstimate } from '@principle-theorem/principle-core';
import {
  ICampaignEntryReportResult,
  ICustomReport,
  IMarketingCampaign,
  IMarketingCampaignRun,
} from '@principle-theorem/principle-core/interfaces';
import {
  CampaignEntryReport,
  CampaignEntryReportContext,
} from '@principle-theorem/reporting';
import {
  debounceUserInput,
  firstValueFrom,
  getError,
  shareReplayCold,
  WithRef,
} from '@principle-theorem/shared';
import * as moment from 'moment-timezone';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { CampaignReportSummaryComponent } from '../campaign-report-summary/campaign-report-summary.component';
import {
  CampaignRunEnrolmentEstimatesComponent,
  ICampaignEnrolment,
} from '../campaign-run-enrolment-estimates/campaign-run-enrolment-estimates.component';
import { MarketingCampaignService } from '../marketing-campaign.service';

export interface ICampaignRunReportDialogRequest {
  campaign: WithRef<IMarketingCampaign>;
  campaignRun: WithRef<IMarketingCampaignRun>;
  customReport: WithRef<ICustomReport>;
}

export interface ICampaignRunReportDialogResponse {
  campaign: WithRef<IMarketingCampaign>;
  campaignRun: WithRef<IMarketingCampaignRun>;
  customReport: WithRef<ICustomReport>;
  entries: ICampaignEntryReportResult[];
  datesUsed: IDateFormData;
}

@Component({
  selector: 'pr-campaign-run-report-dialog',
  templateUrl: './campaign-run-report-dialog.component.html',
  styleUrl: './campaign-run-report-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CampaignReportSummaryComponent,
    CampaignRunEnrolmentEstimatesComponent,
    DateRangeFormComponent,
    InformationBoxComponent,
    MatDatepickerModule,
    MatProgressSpinner,
    NgDialogModule,
  ],
})
export class CampaignRunReportDialogComponent {
  private _dialogRef =
    inject<
      MatDialogRef<
        CampaignRunReportDialogComponent,
        ICampaignRunReportDialogResponse
      >
    >(MatDialogRef);
  private _marketingService = inject(MarketingCampaignService);
  data = inject<ICampaignRunReportDialogRequest>(MAT_DIALOG_DATA);
  datePickers: CustomReportDatePickers;
  errorMessage$ = new BehaviorSubject<string | undefined>(undefined);
  loading$ = new BehaviorSubject<boolean>(true);
  enrolment$: Observable<ICampaignEnrolment | undefined>;
  isDisabled$: Observable<boolean>;

  constructor() {
    this.datePickers = new CustomReportDatePickers(
      signal(this.data.customReport.staticFilters)
    );

    const initialValue: IDateFormData = {
      date: moment().startOf('day'),
      dateFrom: moment().startOf('day'),
      dateTo: moment().endOf('day'),
    };

    this.datePickers.formGroup.patchValue(initialValue, {
      emitEvent: false,
    });

    this.enrolment$ = validFormGroupChanges$(this.datePickers.formGroup).pipe(
      debounceUserInput(),
      startWith(initialValue),
      tap(() => {
        this.loading$.next(true);
        this.errorMessage$.next(undefined);
      }),
      switchMap((formData) =>
        this._loadReport(
          this.data.campaign,
          this.data.campaignRun,
          this.data.customReport,
          formData
        )
      ),
      catchError((error) => {
        this.errorMessage$.next(getError(error));
        return of(undefined);
      }),
      tap(() => this.loading$.next(false)),
      shareReplayCold()
    );
    this.isDisabled$ = this.enrolment$.pipe(
      map((enrolment) => !enrolment?.entries?.length)
    );
  }

  async submit(): Promise<void> {
    const enrolment = await firstValueFrom(this.enrolment$);
    if (!enrolment?.entries?.length) {
      return;
    }

    this._dialogRef.close({
      campaign: this.data.campaign,
      campaignRun: this.data.campaignRun,
      customReport: this.data.customReport,
      entries: enrolment.entries,
      datesUsed: this._getDatesUsed(),
    });
  }

  async _loadReport(
    campaign: WithRef<IMarketingCampaign>,
    _campaignRun: WithRef<IMarketingCampaignRun>,
    customReport: WithRef<ICustomReport>,
    formData: IDateFormData
  ): Promise<ICampaignEnrolment> {
    const context = await CampaignEntryReportContext.buildFromCustomReport(
      campaign,
      customReport
    );
    if (!context) {
      throw new Error(
        `Could not build campaign entry report for campaign: ${campaign.ref.id}`
      );
    }
    const potentialEntries =
      await this._marketingService.getCampaignEntryReportData(
        campaign,
        customReport,
        context,
        formData
      );

    const entries = await CampaignEntryReport.getEligibleReportEntries(
      campaign,
      context,
      potentialEntries
    );

    const estimates = await CampaignCostEstimate.calculateEstimates(
      campaign,
      entries,
      campaign.maximumEntriesPerRun
    );
    return {
      entries,
      skippedEntries: potentialEntries.length - entries.length,
      campaignEntriesInPreview: 0,
      estimates,
    };
  }

  private _getDatesUsed(): IDateFormData {
    const datesUsed: IDateFormData = this.datePickers.formGroup.value;
    if (!this.datePickers.dateFilters.length) {
      datesUsed.date = undefined;
    }
    if (!this.datePickers.dateRangeFilters.length) {
      datesUsed.dateFrom = undefined;
      datesUsed.dateTo = undefined;
    }
    return datesUsed;
  }
}
